// Component: SignalActivity.jsx
// Purpose: Signal -> order activity for the Monitoring tab — the [Orders | Signals] activity card
//          and the signal timeline (placed signals linked to their order, repeated rejections
//          collapsed into one node that expands to a sub-tree, counts on the filter chips).
//          Fed by utils/omsOutcomes.js's deriveOmsOutcomes merge.

import { useMemo, useState } from 'react';
import { ModeBadge } from '../../../components/trading/OrdersTable';
import { rejectReasonLabel, fullRejectReason } from '../../../utils/rejectReason';
import { _fmtTimeIST, _fmtPrice, _istDateKey, _istTimeFmt } from '../../../utils/tradingTransforms';

//* `bracket:<id>` signal ids are the SL/TP legs of a bracket order, not strategy signals. A
//  placed leg already shows inside its trade card (Orders); a REJECTED leg stays in the signal
//  list (a refused protective leg must never be hidden).
const BRACKET_PREFIX = 'bracket:';

function reasonLabel(outcome) {
  //* R8.9 — reuse rejectReasonLabel() verbatim, never a local humanizer.
  return rejectReasonLabel(fullRejectReason(outcome)) ?? outcome?.reject_reason ?? 'unknown';
}

function toMillis(ts) {
  const t = ts ? new Date(ts).getTime() : NaN;
  return Number.isFinite(t) ? t : -Infinity;
}

//--> Bare IST HH:MM (used inside a group whose header already carries the date).
function istTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isFinite(d.getTime()) ? _istTimeFmt.format(d) : '';
}

//--> Group time span: "10:35–17:50" today, "15 Oct · 10:35–17:50" on one earlier day,
//    full "15 Oct, 10:35–16 Oct, 09:20" only when the run crosses days.
function spanLabel(oldestTs, newestTs) {
  if (!newestTs) return '';
  const oldest = new Date(oldestTs ?? newestTs);
  const newest = new Date(newestTs);
  if (_istDateKey(oldest) !== _istDateKey(newest)) return `${_fmtTimeIST(oldestTs)}–${_fmtTimeIST(newestTs)}`;
  const times = oldest.getTime() === newest.getTime() ? istTime(newestTs) : `${istTime(oldestTs)}–${istTime(newestTs)}`;
  if (_istDateKey(newest) === _istDateKey(new Date())) return times;
  const day = _fmtTimeIST(newestTs).split(',')[0];
  return `${day} · ${times}`;
}

/*>>> PURE — omsOutcomes Map -> { items, summary }. Items are newest-first; consecutive
   rejections with the same humanized reason collapse into one group item. <<<*/
export function buildSignalActivity(omsOutcomes) {
  const rows = [];
  if (omsOutcomes instanceof Map) {
    for (const [key, outcome] of omsOutcomes.entries()) {
      const isBracketLeg = String(key).startsWith(BRACKET_PREFIX);
      if (isBracketLeg && outcome?.kind === 'placed') continue;
      rows.push({ key, outcome, isBracketLeg });
    }
  }
  rows.sort((a, b) => toMillis(b.outcome?.created_at) - toMillis(a.outcome?.created_at));

  const summary = { total: rows.length, placed: 0, rejected: 0, atRisk: 0 };
  const items = [];
  for (const row of rows) {
    const kind = row.outcome?.kind;
    if (kind === 'placed') summary.placed += 1;
    if (kind === 'rejected') summary.rejected += 1;
    if (row.outcome?.at_risk) summary.atRisk += 1;

    const prev = items[items.length - 1];
    const label = kind === 'rejected' ? reasonLabel(row.outcome) : null;
    if (label && prev?.kind === 'rejected' && prev.label === label) {
      prev.rows.push(row);
    } else {
      items.push({ key: row.key, kind, label, rows: [row] });
    }
  }
  return { items, summary };
}

/*>>> SMALL PIECES <<<*/
function SideBadge({ side }) {
  const s = String(side ?? '').toUpperCase();
  if (!s) return null;
  const isBuy = s === 'BUY' || s === 'LONG';
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isBuy ? 'text-profit bg-profit/[0.10]' : 'text-loss bg-loss/[0.10]'}`}>
      {isBuy ? '▲ Buy' : '▼ Sell'}
    </span>
  );
}

function OutcomeBadge({ kind }) {
  if (kind === 'placed') {
    return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-profit bg-profit/[0.12]">✓ Placed</span>;
  }
  if (kind === 'rejected') {
    return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-loss bg-loss/[0.12]">✕ Rejected</span>;
  }
  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-fg-2 bg-bg-3">No outcome recorded</span>;
}

function AtRiskBadge({ pulse = true }) {
  return (
    <span data-testid="rejection-at-risk-badge" className={`text-[10px] font-bold px-1.5 py-0.5 rounded border text-loss border-loss/60 bg-loss/[0.12] ${pulse ? 'motion-safe:animate-pulse' : ''}`}>
      ⚠ AT RISK
    </span>
  );
}

//* R8.10 — mode badge only when it says something: always for UNATTRIBUTED, otherwise only
//  when the row's mode differs from the deployment's own mode.
function RowModeBadge({ mode, deploymentMode }) {
  const m = String(mode ?? '').toUpperCase();
  if (!m) return null;
  if (m === 'UNATTRIBUTED') return <ModeBadge mode="UNATTRIBUTED" />;
  if (deploymentMode && m === String(deploymentMode).toUpperCase()) return null;
  if (m === 'LIVE' || m === 'PAPER') return <ModeBadge mode={m} />;
  return null;
}

//--> #104 R9/§8.3 classification badges (moved from the retired RejectionRiskFeed card).
function ClassificationBadges({ o }) {
  return (
    <>
      {o.failure_type_label && (
        <span
          data-testid="rejection-failure-type-badge"
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
            o.at_risk ? 'text-loss border-loss/40 bg-loss/[0.06]' : 'text-fg-2 border-[var(--divider)] bg-bg-2/40'
          }`}
        >
          {o.failure_type_label}
        </span>
      )}
      {o.origin_label && (
        <span data-testid="rejection-origin-badge" className="text-[10px] font-medium px-1.5 py-0.5 rounded border text-fg-3 border-[var(--divider)] bg-bg-2/20">
          {o.origin_label === 'Internal' ? 'internal' : o.origin_label === 'Broker' ? 'broker' : o.origin_label}
        </span>
      )}
      {o.historical_incomplete && (
        <span data-testid="rejection-historical-badge" className="text-[10px] italic px-1.5 py-0.5 rounded border text-fg-3 border-[var(--divider)] bg-bg-2/10">
          {o.execution_mode_display}
        </span>
      )}
    </>
  );
}

function SignalDetail({ o, rowKey }) {
  return (
    <div data-testid="signal-activity-detail" className="pb-2 flex flex-col gap-1 text-[11px] text-fg-2">
      {o.kind === 'rejected' && <span>Rejected — {reasonLabel(o)}</span>}
      {o.kind === 'rejected' && <span className="text-fg-3">No order created</span>}
      <span className="flex flex-wrap gap-1.5"><ClassificationBadges o={o} /></span>
      <span className="font-mono text-[10px] text-fg-3 break-all">{rowKey}</span>
    </div>
  );
}

function Chevron({ open }) {
  return (
    <span aria-hidden className={`inline-block text-[10px] text-fg-3 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}>▸</span>
  );
}

//* Timeline dot on the main spine — green placed, red rejected, pulsing ring when at risk.
function TimelineDot({ kind, atRisk }) {
  const fill = kind === 'placed' ? 'bg-profit' : kind === 'rejected' ? 'bg-loss' : 'bg-fg-3';
  return (
    <span
      aria-hidden
      className={`absolute left-[4px] top-[15px] h-[11px] w-[11px] rounded-full border-2 border-bg-2 ${fill} ${atRisk ? 'ring-2 ring-loss/40 motion-safe:animate-pulse' : ''}`}
    />
  );
}

/*>>> ONE SIGNAL — a node on the main timeline <<<*/
function SignalRow({ row, deploymentMode }) {
  const [open, setOpen] = useState(false);
  const o = row.outcome ?? {};
  const qtyPrice = o.qty != null ? `${o.qty}${o.price != null ? ` @ ${_fmtPrice(o.price)}` : ''}` : null;
  return (
    <div data-testid="signal-activity-row" className="relative pl-6">
      <TimelineDot kind={o.kind} atRisk={o.at_risk} />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[44px] py-2 text-left flex flex-col gap-1 rounded-md hover:bg-bg-2/40 transition-colors px-1.5 -mx-1.5"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-mono tabular-nums text-fg-3 shrink-0">{_fmtTimeIST(o.created_at) || '—'}</span>
          <SideBadge side={o.side} />
          {row.isBracketLeg && <span className="text-[10px] text-fg-3 shrink-0">SL/TP leg</span>}
          {qtyPrice && <span className="text-[11px] tabular-nums text-fg-2 truncate">{qtyPrice}</span>}
          <span className="ml-auto flex items-center gap-1.5 shrink-0">
            {o.at_risk && <AtRiskBadge />}
            <span data-testid="oms-outcome-chip" className="flex items-center gap-1.5">
              <OutcomeBadge kind={o.kind} />
              <RowModeBadge mode={o.execution_mode} deploymentMode={deploymentMode} />
            </span>
          </span>
        </span>
        {o.kind === 'placed' && (
          <span className="text-[11px] text-fg-3 truncate">
            → Order {o.order_id ? String(o.order_id).slice(0, 8) : ''}
            {o.order_status && <span className="text-fg-2"> · {o.order_status}</span>}
          </span>
        )}
        {o.kind === 'rejected' && (
          <span className="text-[11px] text-loss/90 truncate" title={reasonLabel(o)}>{reasonLabel(o)}</span>
        )}
      </button>
      {open && <SignalDetail o={o} rowKey={row.key} />}
    </div>
  );
}

/*>>> ONE SIGNAL INSIDE A GROUP — a leaf on the group's sub-tree. The group header already
   says "Rejected" + the reason + the date, so the leaf carries only what differs. <<<*/
function GroupLeaf({ row, deploymentMode }) {
  const [open, setOpen] = useState(false);
  const o = row.outcome ?? {};
  const qtyPrice = o.qty != null ? `${o.qty}${o.price != null ? ` @ ${_fmtPrice(o.price)}` : ''}` : null;
  return (
    <div
      data-testid="signal-activity-row"
      //* branch connector from the sub-tree spine into this leaf
      className="relative before:content-[''] before:absolute before:-left-[13px] before:top-[19px] before:w-[9px] before:h-px before:bg-[var(--divider)]"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[38px] py-1.5 px-1.5 text-left flex items-center gap-2 rounded-md hover:bg-bg-2/40 transition-colors"
      >
        <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-loss/70" />
        <span className="text-[11px] font-mono tabular-nums text-fg-3 shrink-0">{istTime(o.created_at) || '—'}</span>
        <SideBadge side={o.side} />
        {qtyPrice && <span className="text-[11px] tabular-nums text-fg-2 truncate">{qtyPrice}</span>}
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          {o.at_risk && <AtRiskBadge />}
          <span data-testid="oms-outcome-chip" className="flex items-center gap-1.5">
            <RowModeBadge mode={o.execution_mode} deploymentMode={deploymentMode} />
          </span>
          <Chevron open={open} />
        </span>
      </button>
      {open && <div className="pl-5"><SignalDetail o={o} rowKey={row.key} /></div>}
    </div>
  );
}

/*>>> REPEATED REJECTIONS — one timeline node, ×N, reason once; expands to a sub-tree <<<*/
function RejectGroup({ item, deploymentMode }) {
  const [open, setOpen] = useState(false);
  const times = item.rows.map((r) => r.outcome?.created_at).filter(Boolean);
  const anyAtRisk = item.rows.some((r) => r.outcome?.at_risk);
  return (
    <div data-testid="signal-reject-group" className="relative pl-6">
      <TimelineDot kind="rejected" atRisk={anyAtRisk} />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[44px] py-2 text-left flex flex-col gap-1 rounded-md hover:bg-bg-2/40 transition-colors px-1.5 -mx-1.5"
      >
        <span className="flex items-center gap-2">
          <OutcomeBadge kind="rejected" />
          <span className="text-[11px] font-bold tabular-nums text-loss">×{item.rows.length}</span>
          <span className="text-[11px] font-mono tabular-nums text-fg-3 truncate">{spanLabel(times[times.length - 1], times[0])}</span>
          <span className="ml-auto flex items-center gap-1.5 shrink-0">
            {anyAtRisk && <AtRiskBadge pulse={false} />}
            <Chevron open={open} />
          </span>
        </span>
        <span className="text-[11px] text-loss/90">{item.label}</span>
      </button>
      {open && (
        //* sub-tree spine — leaves branch off it (GroupLeaf connector)
        <div className="relative ml-1 mt-0.5 mb-2 pl-4 before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-[19px] before:w-px before:bg-[var(--divider)]">
          {item.rows.map((row) => (
            <GroupLeaf key={row.key} row={row} deploymentMode={deploymentMode} />
          ))}
        </div>
      )}
    </div>
  );
}

const FILTERS = [
  { id: 'all', label: 'All', countKey: 'total', countClass: 'text-fg-2 bg-bg-2' },
  { id: 'placed', label: 'Placed', countKey: 'placed', countClass: 'text-profit bg-profit/[0.12]' },
  { id: 'rejected', label: 'Rejected', countKey: 'rejected', countClass: 'text-loss bg-loss/[0.12]' },
];

/*>>> SIGNAL TIMELINE — filter chips carry the counts; nodes on a vertical spine below. <<<*/
export function SignalActivityList({ omsOutcomes, unavailable = false, deploymentMode }) {
  const [filter, setFilter] = useState('all');
  const { items, summary } = useMemo(() => buildSignalActivity(omsOutcomes), [omsOutcomes]);
  const visible = filter === 'all' ? items : items.filter((it) => it.kind === filter);
  return (
    <div data-testid="signal-activity-list" className="flex flex-col gap-3">
      {unavailable && (
        <div data-testid="rejection-data-unavailable" role="status" className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-[11px] text-fg-2">
          ⚠ Rejection data unavailable — signal outcomes may be incomplete (a rejected signal could be missing from this view).
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter signals">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={`inline-flex items-center gap-1.5 min-h-[32px] pl-3 pr-1.5 rounded-full text-[11px] font-semibold transition-colors duration-150 ${
              filter === f.id ? 'bg-fg-1 text-bg-2' : 'bg-bg-3 text-fg-2 hover:text-fg-1'
            }`}
          >
            {f.label}
            <span data-testid={`signal-filter-count-${f.id}`} className={`min-w-[20px] text-center tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full ${filter === f.id ? 'bg-bg-2 text-fg-1' : f.countClass}`}>
              {summary[f.countKey]}
            </span>
          </button>
        ))}
        {summary.atRisk > 0 && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-full border text-loss border-loss/60 bg-loss/[0.12]">⚠ {summary.atRisk} at risk</span>
        )}
      </div>
      {visible.length === 0 ? (
        <div className="py-6 text-center text-[12px] text-fg-3">No signals yet</div>
      ) : (
        //* the main vertical spine the timeline dots sit on
        <div className="relative flex flex-col before:content-[''] before:absolute before:left-[9px] before:top-3 before:bottom-3 before:w-px before:bg-[var(--divider)]">
          {visible.map((item) =>
            item.kind === 'rejected' && item.rows.length > 1 ? (
              <RejectGroup key={item.key} item={item} deploymentMode={deploymentMode} />
            ) : (
              <SignalRow key={item.key} row={item.rows[0]} deploymentMode={deploymentMode} />
            )
          )}
        </div>
      )}
    </div>
  );
}

/*>>> ACTIVITY CARD — [Orders | Signals] segmented card for the right rail. <<<*/
//* `ordersView` is the caller's orders list (TradeHistory) — passed in so this module stays free of
//  the trading-data context and directly testable.
export function ActivityCard({ tab, onTab, ordersView, omsOutcomes, unavailable, deploymentMode }) {
  const { summary } = useMemo(() => buildSignalActivity(omsOutcomes), [omsOutcomes]);
  const tabs = [
    { id: 'orders', label: 'Orders', count: null },
    { id: 'signals', label: 'Signals', count: summary.total || null },
  ];
  return (
    <div data-testid="activity-card" className="rounded-xl bg-bg-2 shadow-[var(--shadow-sm)] p-3.5 flex flex-col gap-3">
      {/*>>> Segmented tabs — the highlight slides between the two halves <<<*/}
      <div className="relative flex rounded-[10px] bg-bg-3 p-1" role="tablist">
        <span
          aria-hidden
          className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-[7px] bg-bg-2 shadow-[var(--shadow-sm)] transition-transform duration-200 ease-out motion-reduce:transition-none ${
            tab === 'signals' ? 'translate-x-full' : 'translate-x-0'
          }`}
        />
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTab(t.id)}
            className={`relative z-10 flex-1 min-h-[36px] inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-colors duration-150 ${
              tab === t.id ? 'text-fg-1' : 'text-fg-2 hover:text-fg-1'
            }`}
          >
            {t.label}
            {t.count != null && (
              <span className={`tabular-nums text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-bg-3 text-fg-1' : 'bg-bg-2 text-fg-2'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>
      {tab === 'orders' ? (
        ordersView
      ) : (
        <SignalActivityList omsOutcomes={omsOutcomes} unavailable={unavailable} deploymentMode={deploymentMode} />
      )}
    </div>
  );
}
